
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SearchInput from '../../components/base/SearchInput';
import { trendingTracks } from '../../mocks/musicData';
import { useMusicPlayer } from '../../hooks/useMusicPlayer';

export default function BrowsePage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState('All');
  const [selectedMood, setSelectedMood] = useState('All');
  const [sortBy, setSortBy] = useState('popularity');
  const [likedTracks, setLikedTracks] = useState<number[]>([2, 4, 7]);
  const { playTrack } = useMusicPlayer();
  const navigate = useNavigate();

  const genres = ['All', 'Pop', 'Rock', 'Hip Hop', 'Electronic', 'Jazz', 'Classical', 'R&B', 'Country'];
  const moods = ['All', 'Happy', 'Chill', 'Energetic', 'Sad', 'Romantic', 'Party', 'Focus'];
  const sortOptions = [
    { value: 'popularity', label: 'Most Popular' },
    { value: 'recent', label: 'Recently Added' },
    { value: 'title', label: 'Title A-Z' },
    { value: 'artist', label: 'Artist A-Z' }
  ];

  const filteredTracks = trendingTracks.filter(track => {
    const matchesSearch = track.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         track.artist.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         track.album.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesGenre = selectedGenre === 'All' || track.genre === selectedGenre;
    const matchesMood = selectedMood === 'All' || track.mood === selectedMood;
    return matchesSearch && matchesGenre && matchesMood;
  });

  const sortedTracks = [...filteredTracks].sort((a, b) => {
    switch (sortBy) {
      case 'title':
        return a.title.localeCompare(b.title);
      case 'artist':
        return a.artist.localeCompare(b.artist);
      case 'recent':
        return new Date(b.releaseDate || '2024-01-01').getTime() - new Date(a.releaseDate || '2024-01-01').getTime();
      default:
        return parseInt(b.plays.replace(/[^0-9]/g, '')) - parseInt(a.plays.replace(/[^0-9]/g, ''));
    }
  });

  const toggleLike = (trackId: number) => {
    setLikedTracks(prev => 
      prev.includes(trackId) 
        ? prev.filter(id => id !== trackId)
        : [...prev, trackId]
    );
  };

  const handlePlayTrack = (track: any) => {
    playTrack(track);
  };

  const handleTrackSelect = (track: any) => {
    playTrack(track);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header onTrackSelect={handleTrackSelect} />
      
      <main className="pt-8 pb-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-r from-purple-600 to-pink-600 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-4">Browse Music</h1>
            <p className="text-xl text-purple-100 mb-8">Discover new tracks, artists, and genres</p>
            
            <div className="max-w-md mx-auto">
              <SearchInput 
                onSearch={setSearchQuery}
                placeholder="Search tracks, artists, albums..."
                className="bg-white"
              />
            </div>
          </div>
        </section>

        {/* Filters */}
        <section className="py-8 bg-white border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {/* Genre Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Genre</label>
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-8"
                >
                  {genres.map(genre => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              </div>

              {/* Mood Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mood</label>
                <select
                  value={selectedMood}
                  onChange={(e) => setSelectedMood(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-8"
                >
                  {moods.map(mood => (
                    <option key={mood} value={mood}>{mood}</option>
                  ))}
                </select>
              </div>

              {/* Sort Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-8"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              {/* Results Count */}
              <div className="flex items-end">
                <p className="text-sm text-gray-600">
                  {sortedTracks.length} track{sortedTracks.length !== 1 ? 's' : ''} found
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Tracks List */}
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {sortedTracks.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
                <div className="p-6">
                  <div className="space-y-2">
                    {sortedTracks.map((track, index) => (
                      <div 
                        key={track.id}
                        className="flex items-center space-x-4 p-4 rounded-xl hover:bg-gray-50 transition-all duration-200 cursor-pointer group"
                      >
                        <div className="flex items-center space-x-4 flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-400 w-8 text-center">
                            {index + 1}
                          </div>
                          <div className="relative">
                            <img 
                              src={track.imageUrl}
                              alt={track.title}
                              className="w-12 h-12 rounded-lg object-cover object-top"
                            />
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 rounded-lg transition-all duration-200 flex items-center justify-center">
                              <button 
                                onClick={() => handlePlayTrack(track)}
                                className="w-6 h-6 bg-white text-purple-600 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 flex items-center justify-center"
                              >
                                <i className="ri-play-fill text-xs"></i>
                              </button>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-gray-900 truncate">{track.title}</h4>
                            <p className="text-sm text-gray-600 truncate">{track.artist}</p>
                          </div>
                        </div>

                        <div className="hidden md:block text-sm text-gray-600 min-w-0 w-32">
                          {track.album}
                        </div>

                        <div className="hidden lg:block text-sm text-gray-500 min-w-0 w-20">
                          {track.genre}
                        </div>

                        <div className="flex items-center space-x-4">
                          <button
                            onClick={() => toggleLike(track.id)}
                            className={`transition-colors cursor-pointer ${
                              likedTracks.includes(track.id) 
                                ? 'text-red-500' 
                                : 'text-gray-400 hover:text-red-500'
                            }`}
                          >
                            <i className={`ri-heart-${likedTracks.includes(track.id) ? 'fill' : 'line'} text-lg`}></i>
                          </button>
                          <div className="text-sm text-gray-500 min-w-0 w-12 text-right">
                            {track.duration}
                          </div>
                          <div className="text-xs text-gray-400 min-w-0 w-16 text-right">
                            {track.plays}
                          </div>
                          <button className="text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
                            <i className="ri-more-2-fill text-lg"></i>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center py-16">
                <i className="ri-search-line text-6xl text-gray-300 mb-4 block"></i>
                <h3 className="text-xl font-semibold text-gray-600 mb-2">No tracks found</h3>
                <p className="text-gray-500">Try adjusting your search or filters</p>
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
